import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, fileUrl } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Trash2, CheckCircle2, XCircle, Flag, Play, Loader2 } from "lucide-react";
import { toast } from "sonner";

function timeAgo(iso) {
  if (!iso) return "";
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
  return `${Math.floor(diff / 2592000)}mo ago`;
}

export default function AdminReports() {
  const [tab, setTab] = useState("pending");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = () => {
    setLoading(true);
    api.get("/admin/reports", { params: { status: tab } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [tab]);

  const approve = async (id) => {
    if (!window.confirm("Delete the reported content? This will notify the author.")) return;
    setBusy(id);
    try {
      await api.post(`/admin/reports/${id}/approve`);
      toast.success("Content removed");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  const dismiss = async (id) => {
    setBusy(id);
    try {
      await api.post(`/admin/reports/${id}/dismiss`);
      toast.success("Report dismissed");
      load();
    } catch { toast.error("Failed"); }
    finally { setBusy(null); }
  };

  const TABS = [
    ["pending", "Pending"],
    ["removed", "Removed"],
    ["dismissed", "Dismissed"],
  ];

  return (
    <div className="p-6 lg:p-10">
      <div className="border-b border-border pb-6 mb-8">
        <div className="label-mono text-muted-foreground mb-1">Moderation</div>
        <h1 className="font-serif text-4xl mb-6 flex items-center gap-3">
          <Flag className="w-8 h-8 text-[hsl(var(--oppose))]" strokeWidth={2} />
          Reports
        </h1>
        <div className="border border-border inline-flex">
          {TABS.map(([k, l]) => (
            <button
              key={k}
              onClick={() => setTab(k)}
              data-testid={`admin-reports-tab-${k}`}
              className={`label-mono px-5 py-2.5 transition-colors ${tab === k ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary"}`}
            >{l}</button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="label-mono text-muted-foreground flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : items.length === 0 ? (
        <div className="border border-dashed border-border p-16 text-center label-mono text-muted-foreground" data-testid="admin-reports-empty">
          No {tab} reports.
        </div>
      ) : (
        <div className="grid gap-4">
          {items.map((r) => {
            const detailUrl = r.target_type === "debate" ? `/debates/${r.target_id}` : `/thoughts`;
            return (
              <div key={r.id} className="border border-border rounded-xl p-4 flex items-start gap-4" data-testid={`admin-report-${r.id}`}>
                {/* Thumb */}
                <Link to={detailUrl} className="w-24 h-24 rounded-lg bg-black relative shrink-0 overflow-hidden">
                  {r.target_thumbnail_url ? (
                    <img src={fileUrl(r.target_thumbnail_url)} alt="" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full bg-[hsl(var(--primary-soft))]" />
                  )}
                  <Play className="absolute inset-0 m-auto w-5 h-5 text-white fill-white" />
                </Link>

                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] label-mono px-2 py-0.5 rounded-full uppercase ${r.target_type === "debate" ? "bg-[hsl(var(--primary-soft))] text-[hsl(var(--primary))]" : "bg-muted text-muted-foreground"}`}>
                      {r.target_type}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{timeAgo(r.created_at)}</span>
                    <span className="text-[11px] text-muted-foreground">· reason: <b>{r.reason}</b></span>
                  </div>
                  <Link to={detailUrl} className="text-sm font-semibold hover:underline line-clamp-1">
                    {r.target_title || "Untitled"}
                  </Link>
                  <div className="text-xs text-muted-foreground mt-1 flex flex-wrap gap-x-4 gap-y-1">
                    <span>Owner: <Link className="hover:underline" to={`/profile/${r.owner?.username}`}>@{r.owner?.username || "unknown"}</Link></span>
                    <span>Reporter: <Link className="hover:underline" to={`/profile/${r.reporter?.username}`}>@{r.reporter?.username || "unknown"}</Link></span>
                  </div>
                </div>

                {tab === "pending" && (
                  <div className="flex gap-2 shrink-0">
                    <Button
                      size="sm" variant="outline"
                      className="rounded-sm h-8 hover:bg-[hsl(var(--oppose))] hover:text-white"
                      onClick={() => approve(r.id)}
                      disabled={busy === r.id}
                      data-testid={`admin-report-remove-${r.id}`}
                    >
                      <Trash2 className="w-3 h-3 mr-1" /> Remove
                    </Button>
                    <Button
                      size="sm" variant="outline"
                      className="rounded-sm h-8"
                      onClick={() => dismiss(r.id)}
                      disabled={busy === r.id}
                      data-testid={`admin-report-dismiss-${r.id}`}
                    >
                      <XCircle className="w-3 h-3 mr-1" /> Dismiss
                    </Button>
                  </div>
                )}
                {tab === "removed" && (
                  <div className="text-xs text-[hsl(var(--oppose))] font-semibold flex items-center gap-1 shrink-0">
                    <CheckCircle2 className="w-4 h-4" /> Removed
                  </div>
                )}
                {tab === "dismissed" && (
                  <div className="text-xs text-muted-foreground flex items-center gap-1 shrink-0">
                    <XCircle className="w-4 h-4" /> Dismissed
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
